import { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  CircularProgress,
  Stack,
  Button,
} from '@mui/material';
import { HistoryEduOutlined, DownloadRounded } from '@mui/icons-material';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { collection, onSnapshot, query, where, DocumentData } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useTenantStore } from '../../../stores/tenant.store';
import { useTranslation } from 'react-i18next';
import { useTenantConfig } from '../../../hooks/use-tenant-config';
import { formatGrade } from '../../../lib/grading';
import { courseLabel, enrollmentStatusLabel } from './student-transcript.model';
import { formatDate } from '../../../lib/i18n-format';
import { errorText } from '../../../lib/error-text';

interface StudentTranscriptProps {
  studentId: string;
  studentName: string;
}

export function StudentTranscript({ studentId, studentName }: StudentTranscriptProps) {
  const { currentTenantId } = useTenantStore();
  const { t } = useTranslation('sis');
  const { gradingScale } = useTenantConfig();

  const [enrollments, setEnrollments] = useState<DocumentData[]>([]);
  const [sections, setSections] = useState<DocumentData[]>([]);
  const [courses, setCourses] = useState<DocumentData[]>([]);
  const [terms, setTerms] = useState<DocumentData[]>([]);
  const [loading, setLoading] = useState(false);
  /** La lectura de inscripciones rechazada. Sin esto se veía «sin
   *  calificaciones», que es otra afirmación. */
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!currentTenantId || !studentId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    // Fetch user's enrollments
    const qEnrollments = query(
      collection(db, `tenants/${currentTenantId}/enrollments`),
      where('studentId', '==', studentId),
    );
    setLoadError(null);
    const unsubEnrollments = onSnapshot(
      qEnrollments,
      (snapshot) => {
        setEnrollments(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (e) => {
        setLoadError(errorText(e));
        setLoading(false);
      },
    );

    // Reference collections for resolution
    const unsubSections = onSnapshot(collection(db, `tenants/${currentTenantId}/sections`), (s) =>
      setSections(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
    const unsubCourses = onSnapshot(collection(db, `tenants/${currentTenantId}/courses`), (s) =>
      setCourses(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
    const unsubTerms = onSnapshot(collection(db, `tenants/${currentTenantId}/terms`), (s) => {
      setTerms(s.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });

    return () => {
      unsubEnrollments();
      unsubSections();
      unsubCourses();
      unsubTerms();
    };
  }, [currentTenantId, studentId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return <Alert severity="error">{loadError}</Alert>;
  }

  if (enrollments.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
        <HistoryEduOutlined sx={{ fontSize: 60, opacity: 0.2, mb: 2 }} />
        <Typography variant="h6">{t('people.profile.grades.empty')}</Typography>
        <Typography variant="body2">{t('people.profile.grades.emptyDesc')}</Typography>
      </Box>
    );
  }

  const resolveSection = (sectionId: string) => sections.find((s) => s.id === sectionId);
  const resolveCourse = (e: DocumentData) =>
    courseLabel(
      resolveSection(e.sectionId) as { courseId?: string } | undefined,
      courses as { id: string; name?: string }[],
      e.sectionId,
    );
  const resolveTerm = (termId: string) => terms.find((t) => t.id === termId);

  const toMs = (v: any): number => {
    if (!v) return 0;
    if (typeof v.toDate === 'function') return v.toDate().getTime();
    return new Date(v).getTime();
  };

  /** El certificado imprime fechas cortas; el idioma lo pone la app. */
  const shortDate = (v: unknown): string =>
    formatDate(v as Parameters<typeof formatDate>[0], {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    }) || '-';

  /** Enrollments sorted newest section start date first. */
  const sortedEnrollments = [...enrollments].sort((a, b) => {
    const secA = resolveSection(a.sectionId);
    const secB = resolveSection(b.sectionId);
    return toMs(secA?.startDate) - toMs(secB?.startDate);
  });

  // Render Transcript
  const handleExportPDF = () => {
    const doc = new jsPDF();

    // Title
    doc.setFontSize(18);
    doc.text(t('people.profile.grades.title'), 14, 22);

    // Subtitle
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(t('people.profile.grades.pdfStudent', { name: studentName }), 14, 30);
    doc.text(t('people.profile.grades.pdfIssued', { date: shortDate(new Date()) }), 14, 36);

    // Table Data
    const tableData = sortedEnrollments.map((e) => {
      const sec = resolveSection(e.sectionId);
      const courseName = resolveCourse(e);
      const termName = sec ? resolveTerm(sec.termId)?.name || '--' : '--';
      const dateResolved = e.status !== 'enrolled' ? shortDate(e.updatedAt?.toDate?.()) : '-';
      const grade = formatGrade({ value: e.grade, label: e.gradeLabel }, gradingScale);
      const status = enrollmentStatusLabel(e.status, t);

      return [courseName, termName, dateResolved, grade, status];
    });

    autoTable(doc, {
      startY: 45,
      head: [
        [
          t('people.profile.grades.columns.course'),
          t('people.profile.grades.columns.term'),
          t('people.profile.grades.columns.resolvedAt'),
          t('people.profile.grades.columns.finalGrade'),
          t('people.profile.grades.columns.status'),
        ],
      ],
      body: tableData,
    });

    doc.save(`historial_${studentName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`);
  };

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {t('people.profile.grades.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('people.profile.grades.subtitle')}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<DownloadRounded />} onClick={handleExportPDF}>
          {t('people.profile.grades.exportPdf')}
        </Button>
      </Stack>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ '& th': { color: 'text.secondary', fontWeight: 600 } }}>
              <TableCell>{t('people.profile.grades.columns.course')}</TableCell>
              <TableCell>{t('people.profile.grades.columns.term')}</TableCell>
              <TableCell>{t('people.profile.grades.columns.start')}</TableCell>
              <TableCell>{t('people.profile.grades.columns.end')}</TableCell>
              <TableCell>{t('people.profile.grades.columns.finalGrade')}</TableCell>
              <TableCell>{t('people.profile.grades.columns.status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedEnrollments.map((e) => {
              const sec = resolveSection(e.sectionId);
              const courseName = resolveCourse(e);
              const termName = sec ? resolveTerm(sec.termId)?.name || '--' : '--';

              return (
                <TableRow key={e.id} hover>
                  <TableCell>
                    <Typography fontWeight={600} color="text.primary">
                      {courseName}
                    </Typography>
                  </TableCell>
                  <TableCell>{termName}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: 13, whiteSpace: 'nowrap' }}>
                    {shortDate(sec?.startDate)}
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: 13, whiteSpace: 'nowrap' }}>
                    {shortDate(sec?.endDate)}
                  </TableCell>
                  <TableCell>
                    <Typography
                      fontWeight={700}
                      color={e.status === 'approved' ? 'success.main' : 'text.primary'}
                    >
                      {formatGrade({ value: e.grade, label: e.gradeLabel }, gradingScale)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={enrollmentStatusLabel(e.status, t)}
                      color={
                        e.status === 'approved'
                          ? 'success'
                          : e.status === 'failed'
                            ? 'error'
                            : e.status === 'dropped'
                              ? 'warning'
                              : 'default'
                      }
                      sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
